import { ChangeDetectionStrategy, Component, inject, input, model, OnChanges, signal, SimpleChanges, NgModule } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { AppConfig } from 'src/app/app-config.loader';
import { AnagraficaIndirizziService } from 'src/app/core/anagrafica-indirizzi.service';
import { AnagraficheService } from 'src/app/core/anagrafiche.service';
import { DocumentiService } from 'src/app/core/documenti.service';
import { RegistriService } from 'src/app/core/registri.service';
import { DocumentiSearchDocRow } from 'src/app/shared/models/documento-search.model';
import { HeaderDistruzioneOperazioniMassive, SummaryDocumentoToDestroy } from '../models/operazioni-massive.model';
import { OperazioniMassiveService } from '../services/operazioni-massive.service';
import { ConfirmationService, Message, MessageService } from 'primeng/api';


@Component({
  selector: 'app-riepilogo-distruzione',
  changeDetection: ChangeDetectionStrategy.OnPush,  
  template: `
    <div class="grid ">

      <div class="col-4">
        <p-card class="ml-2">
          <p class="m-0">Numero di Ordine di Servizio :
              @if (header().ordine) {
                 <span class= "font-bold">{{header().ordine}}</span>
              }
              @else{
                ---------
              }
          </p>
          <p class="m-0">Data Distruzione Documenti : 
              @if (header().dataDistruzione) {
                <span class="font-bold">{{header().dataDistruzione | date: 'dd/MM/yyyy'}}</span>
              }
              @else{
                --/--/----
              }
          </p>
          <p class="m-0">Mezzo di Distruzione : 
              @if (header().mezzoDistruzione) {
                <span class="font-bold">{{header().mezzoDistruzione}}</span>
              }
              @else{
                ---------
              } 
          </p>
          @if ( protocol()) {
            <p class="mx-0 mt-2">Numero di Ordine del Verbale di Distruzione: <span class="font-bold text-red-500">{{protocol()}}</span></p>
          }
        </p-card>
      </div>
      
      <div class="col-12">
        <p-table [value]="documents()" styleClass="p-datatable-gridlines">
          <ng-template pTemplate="header">
              <tr>
                  <th class="text-center w-1">Qualifica e classifica di segretezza del documento</th>
                  <th class="text-center w-2">Numero di protocollo o di controllo del documento</th>
                  <th class="text-center w-3">Ente originatore del documento</th>
                  <th class="text-center w-1">Numero distintivo di copia del documento</th>
                  <th class="text-center w-1">Lingua in cui il documento è redatto</th>
                  <th class="text-center w-1">Numero corrispondente del registro di carico o (per le Pubblicazioni classificate) del tallone di carico</th>
              </tr>
          </ng-template>
          <ng-template pTemplate="body" let-item>
              <tr>
                  <td class="text-center">{{ item.classifica }} {{ item.qualifica }}</td>
                  <td class="text-center">{{ item.protocollo }}</td>
                  <td class="text-center">{{ item.ente }}</td>
                  <td class="text-center">{{ item.numeroDistintivoCopia }}</td>
                  <td class="text-center">{{ item.lingua }}</td>
                  <td class="text-center">{{ item.numeroOrdine }}</td>
              </tr>
          </ng-template>
        </p-table>
      </div>
      <div class="col-12 buttons flex justify-content-end gap-2">
        <button pButton type="button" icon="fas fa-trash" label="Conferma" title="Conferma" (click)="confirmDestroy()" class="p-button-danger" [disabled]="!!operazioneId()"></button>
        <button pButton type="button" icon="fas fa-search" label="Visualizza Verbale" title="Visualizza Verbale" (click)="openPreview()" [disabled]="!isDeletingOK()" class="p-button-info"></button>
      </div>       
    </div>
    <app-verbale-distruzione-dialog [(isOpen)]="isPreviewOpen" [id]="operazioneId()" [protocol]="protocol()" [header]="header()" [documents]="documents()"/>
  `
})
export class RiepilogoDistruzioneComponent implements OnChanges{
  
  private readonly documentiService = inject(DocumentiService);
  private readonly registriService = inject(RegistriService);
  private readonly anagraficheService = inject(AnagraficheService);
  private readonly anagraficaIndirizziService = inject(AnagraficaIndirizziService)
  private readonly appConfig = inject(AppConfig);
  private readonly confirmationService = inject(ConfirmationService);

  private readonly  messageService= inject(MessageService);

  private readonly operazioniMassiveService = inject(OperazioniMassiveService);
  
  header = input.required<HeaderDistruzioneOperazioniMassive>();
  items = input<DocumentiSearchDocRow[]>([]);
  
  public protocol = signal<string>('');

  public isPreviewOpen = model<boolean>(false);
  public documents = signal<SummaryDocumentoToDestroy[]>([]);

  public isDeletingOK = signal<boolean>(false);

  public operazioneId = signal<number|undefined>(undefined);

  ngOnChanges(changes: SimpleChanges): void {
    
    if ( changes.items) {
      this.ensureItems();
    }
  }
  
  private doDestroy(): void {

    this.isDeletingOK.set(false);
    this.protocol.set('');

    this.operazioniMassiveService.distruzione( this.header(), this.documents())
      .subscribe( res => {
        let message : Message = { severity:'success', summary:'Distruzione Massiva Documenti', detail:'Operazione completata con successo'};

       this.operazioneId.set( res?.operazioneId);

        if (res) {
          this.protocol.set(`${res.numeroOrdine}`);
          this.isDeletingOK.set((res.numeroOrdine !== -1));
        } else {
            message = { severity:'error', summary:'Errore', detail:'Errore nella operazione di distruzione massiva dei documenti selezionati'};
        }
        this.messageService.add(message);
      });
  }

  private cancelDestroy(): void {
    this.isDeletingOK.set(false);
    this.protocol.set('');
  }

  public confirmDestroy(): void {

    this.confirmationService.confirm({
        message: 'Sei sicuro di voler procedere alla distruzione dei documenti selezionati?',
        header: 'Distruzione Massiva di Documenti',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.doDestroy(),
        reject: () => this.cancelDestroy()
    });
  }

  public openPreview(): void {
    this.isPreviewOpen.set(true);
  }

  private ensureItems(): void
  {
     const calls : Observable<SummaryDocumentoToDestroy>[] = [];

     this.items().forEach( item => { 
        calls.push(this.getInfoDocumento(item));
     });

     forkJoin( calls).subscribe( 
      values => {
          this.documents.set(values);
        }
     );
  }

  private getInfoDocumento(item: DocumentiSearchDocRow) : Observable<SummaryDocumentoToDestroy>
  {
    return this.documentiService.getDocumento(item.documentoId)
      .pipe( map( (d)=> {
          const classifica = this.anagraficheService.classifiche.getAsArray().find( c=> c.classificaId === d.classificaId);
          const qualifica = this.anagraficheService.qualifiche.getAsArray().find( c=> c.qualificaId === d.qualificaId);
          const indirizzo = this.anagraficaIndirizziService.getAsArray().find( c=> c.indirizzoId === d.mittenteId);
          const ente = (d.arrivo)?indirizzo?.nomeIndirizzo??'':this.appConfig.text.registri.riceventeCIS;

          const result : SummaryDocumentoToDestroy = { 
            id: d.documentoId, 
            protocollo: d.protocollo, 
            classifica: classifica?.sigla??'',
            qualifica: (qualifica?.nomeQualifica !== 'Nazionale')?qualifica?.nomeQualifica??'':'',
            lingua: item.lingua??'',
            ente: ente??'',       
            numeroDistintivoCopia: d.numeroDistintoCopie  
          }              

          return result;
        }),
        switchMap( (x:SummaryDocumentoToDestroy) => {
        if ( x.classifica !== 'S' && x.classifica !== 'SS'  )
          return of(x);

        return this.registriService.getByDocumentId(x.id).pipe(
                        map( r=> {
                            if ( r.carico?.length) {
                              x.numeroOrdine = r.carico[0].numeroOrdine;
                            }
                            return x;
                        })
                      );
        })
    );
  }
}